/**
 * DateTime Service - Centralized UTC/Local timezone conversion utilities
 *
 * This service handles all DateTime conversions between:
 * - Backend: Instant (ISO 8601 UTC format, e.g., "2025-11-02T05:30:00Z")
 * - Frontend: Local timezone for display
 *
 * Key Principles:
 * 1. Backend stores all timestamps in UTC (Instant type)
 * 2. Frontend displays timestamps in user's local timezone
 * 3. Frontend sends timestamps to backend in UTC (ISO 8601 format)
 * 4. Date-only fields (LocalDate) are NOT converted - they remain as plain dates
 */

/**
 * Convert UTC date/time (from backend) to local Date object for display
 *
 * Handles multiple input formats:
 * - ISO 8601 string: "2025-11-02T05:30:00Z"
 * - Unix timestamp (seconds): 1730520600
 * - Array format: [2025, 11, 2, 5, 30, 0]
 *
 * @param utcDateTime - UTC datetime in various formats
 * @returns Date object in local timezone
 * @throws Error if input is invalid
 */
export const utcToLocal = (
  utcDateTime: string | number | any[] | any,
): Date => {
  if (!utcDateTime) {
    throw new Error("UTC date time is required");
  }

  let date: Date;

  // Helper function to convert array format to Date
  const arrayToDate = (arr: number[]): Date => {
    if (!Array.isArray(arr) || arr.length < 3) {
      throw new Error(
        "Invalid array format - expected at least [year, month, day]",
      );
    }

    // Handle both date-only [year, month, day] and full datetime [year, month, day, hour, minute, second]
    const [year, month, day, hour = 0, minute = 0, second = 0] = arr;
    // Create Date in UTC (month is 0-indexed in JavaScript)
    return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  };

  if (typeof utcDateTime === "number") {
    // Handle Unix timestamp (in seconds with possible decimals)
    // Backend may send timestamps like 1730520600.123456 (seconds with decimals)
    // Convert to milliseconds by multiplying by 1000
    date = new Date(utcDateTime * 1000);
  } else if (typeof utcDateTime === "string") {
    // Handle ISO 8601 string format
    if (utcDateTime.includes("T")) {
      // ISO 8601 format: "2025-11-02T05:30:00Z"
      date = new Date(utcDateTime);
    } else {
      // Try to parse as Unix timestamp string
      const timestamp = parseFloat(utcDateTime);
      if (!isNaN(timestamp)) {
        date = new Date(timestamp * 1000);
      } else {
        // Fallback to Date constructor
        date = new Date(utcDateTime);
      }
    }
  } else if (Array.isArray(utcDateTime)) {
    // Handle array format [year, month, day, hour, minute, second]
    date = arrayToDate(utcDateTime);
  } else if (typeof utcDateTime === "object" && utcDateTime !== null) {
    // Handle object format { year, month, day, hour, minute, second }
    const { year, month, day, hour = 0, minute = 0, second = 0 } = utcDateTime;
    date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  } else {
    // Fallback
    date = new Date(utcDateTime);
  }

  // Validate the resulting date
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid UTC date format: ${JSON.stringify(utcDateTime)}`);
  }

  return date;
};

/**
 * Convert local Date object to UTC ISO 8601 string for backend
 *
 * @param localDateTime - Date object in local timezone
 * @returns ISO 8601 UTC string (e.g., "2025-11-02T05:30:00Z")
 */
export const localToUtc = (localDateTime: Date): string => {
  if (!(localDateTime instanceof Date) || isNaN(localDateTime.getTime())) {
    throw new Error("Invalid Date object");
  }
  return localDateTime.toISOString();
};

/**
 * Get current UTC date/time as ISO 8601 string
 *
 * @returns Current UTC timestamp (e.g., "2025-11-02T05:30:00.123Z")
 */
export const nowUtc = (): string => {
  return new Date().toISOString();
};

/**
 * Get today's date in UTC as ISO date string (date-only, no time)
 *
 * @returns Today's date in UTC (e.g., "2025-11-02")
 */
export const todayUtc = (): string => {
  const now = new Date();
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  )
    .toISOString()
    .split("T")[0];
};

/**
 * Format UTC datetime as local time string
 *
 * @param dateTimeString - UTC datetime string from backend
 * @returns Formatted time string (e.g., "02:30 PM")
 */
export const formatTime = (dateTimeString: string | any): string => {
  if (!dateTimeString) {
    return "N/A";
  }

  try {
    const localDate = utcToLocal(dateTimeString);
    return localDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (error) {
    console.warn("Invalid date format received:", dateTimeString, error);
    return "Invalid Date";
  }
};

/**
 * Format UTC datetime as local date string
 *
 * @param dateTimeString - UTC datetime string from backend
 * @returns Formatted date string (e.g., "11/02/2025")
 */
export const formatDate = (dateTimeString: string | any): string => {
  if (!dateTimeString) {
    return "N/A";
  }

  try {
    const localDate = utcToLocal(dateTimeString);
    return localDate.toLocaleDateString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch (error) {
    console.warn("Invalid date format received:", dateTimeString, error);
    return "Invalid Date";
  }
};

/**
 * Format UTC datetime as local date and time string
 *
 * @param dateTimeString - UTC datetime string from backend
 * @returns Formatted datetime string (e.g., "Nov 2, 2025, 02:30 PM")
 */
export const formatDateTime = (dateTimeString: string | any): string => {
  if (!dateTimeString) {
    return "N/A";
  }

  try {
    const localDate = utcToLocal(dateTimeString);
    return localDate.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (error) {
    console.warn("Invalid date format received:", dateTimeString, error);
    return "Invalid Date";
  }
};

/**
 * Format duration between two UTC datetimes
 *
 * @param startDateTime - Start UTC datetime
 * @param endDateTime - End UTC datetime
 * @returns Formatted duration string (e.g., "2h 30m")
 */
export const formatDuration = (
  startDateTime: string | any,
  endDateTime: string | any,
): string => {
  if (!startDateTime || !endDateTime) {
    return "N/A";
  }

  try {
    const start = utcToLocal(startDateTime);
    const end = utcToLocal(endDateTime);
    const durationMs = end.getTime() - start.getTime();

    if (durationMs < 0) {
      return "Invalid Duration";
    }

    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours === 0) {
      return `${minutes}m`;
    }
    return `${hours}h ${minutes}m`;
  } catch (error) {
    console.warn("Invalid date format for duration:", error);
    return "Invalid Duration";
  }
};

/**
 * Calculate session duration in hours
 *
 * @param startDateTime - Start UTC datetime
 * @param endDateTime - End UTC datetime
 * @returns Duration in hours (decimal)
 */
export const calculateSessionDuration = (
  startDateTime: string | any,
  endDateTime: string | any,
): number => {
  if (!startDateTime || !endDateTime) {
    return 0;
  }

  try {
    const start = utcToLocal(startDateTime);
    const end = utcToLocal(endDateTime);
    const durationMs = end.getTime() - start.getTime();

    if (durationMs < 0) {
      return 0;
    }

    return durationMs / (1000 * 60 * 60); // Convert to hours
  } catch (error) {
    console.warn("Invalid date format for duration calculation:", error);
    return 0;
  }
};

/**
 * Parse date-only string (LocalDate from backend) - NO timezone conversion
 *
 * Date-only fields should NOT be converted to/from UTC.
 * They represent calendar dates without time or timezone.
 *
 * @param dateString - Date string in ISO format (e.g., "2025-11-02")
 * @returns Date object (time set to midnight local time)
 */
export const parseDateOnly = (dateString: string): Date => {
  if (!dateString) {
    throw new Error("Date string is required");
  }

  // Parse as local date (no timezone conversion)
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Format date-only value for backend (LocalDate) - NO timezone conversion
 *
 * @param date - Date object
 * @returns ISO date string (e.g., "2025-11-02")
 */
export const formatDateOnly = (date: Date): string => {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error("Invalid Date object");
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export default {
  utcToLocal,
  localToUtc,
  nowUtc,
  todayUtc,
  formatTime,
  formatDate,
  formatDateTime,
  formatDuration,
  calculateSessionDuration,
  parseDateOnly,
  formatDateOnly,
};
